import { useEffect, useRef, useState } from 'react';
import { Fingerprint, Loader2, ScanFace, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/notify';
import { fetchSignInOptions, useAuth } from '@/contexts/AuthContext';
import {
  clearPasskeyOffer, describePasskeyError, hasPasskeyOffer, hasPlatformAuthenticator, isPasskeySupported, passkeyLabel,
  readPasskeyChoice, writePasskeyChoice,
} from '@/components/entry/passkeys';
import { getErrorMessage } from '@/lib/errors';

const isHandheld = () => typeof navigator !== 'undefined'
  && (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '') || (/Macintosh/i.test(navigator.userAgent || '') && navigator.maxTouchPoints > 1));

/** "iPhone", "Android phone", … — the default name of a passkey made on this device. */
export function deviceName(userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '') {
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)) return 'iPad';
  if (/Android/i.test(userAgent)) return /Mobile/i.test(userAgent) ? 'Android phone' : 'Android tablet';
  if (/Windows/i.test(userAgent)) return 'Windows PC';
  if (/Macintosh/i.test(userAgent)) return 'Mac';
  return 'This device';
}

/**
 * "Use Face ID next time?" (01 #9) — once, right after a password sign-in on a phone or tablet that has Face ID /
 * a fingerprint reader, when passkeys are switched on and this person has not set one up (or said "Not now") on
 * this device. Setting it up takes one Face ID / fingerprint confirmation.
 *
 * A small non-modal dialog (the page behind stays usable): when it opens, keyboard / screen-reader focus moves to
 * its main button, so it is announced and reachable; Escape means "Not now"; on close focus goes back to where it was.
 */
export function PasskeyOffer() {
  const { user, registerPasskey } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const primaryRef = useRef(null);
  const returnFocusRef = useRef(null);
  const userId = user?.id;
  const word = passkeyLabel();
  const label = word === 'a passkey' ? 'a passkey' : word;
  const Icon = word === 'Face ID' ? ScanFace : Fingerprint;

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      // Only right after a password sign-in in this tab, and only where a passkey can work. The flag is cleared
      // once the check has finished (not before: a re-run of this effect must still see it).
      if (!hasPasskeyOffer()) return;
      let show = isPasskeySupported() && isHandheld() && !readPasskeyChoice(userId) && await hasPlatformAuthenticator();
      if (show) show = (await fetchSignInOptions()).passkeys.enabled;
      if (cancelled) return;
      clearPasskeyOffer();
      if (show) setOpen(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!open) return undefined;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    returnFocusRef.current = active && active !== document.body ? active : null;
    primaryRef.current?.focus();
    return () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    };
  }, [open]);

  if (!open) return null;

  const notNow = () => {
    writePasskeyChoice(userId, 'dismissed');
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && !saving) {
      event.stopPropagation();
      notNow();
    }
  };

  const setUp = async () => {
    setSaving(true);
    try {
      await registerPasskey({ name: deviceName() });
      writePasskeyChoice(userId, 'registered');
      setOpen(false);
      notify.success(`${word === 'a passkey' ? 'Passkey' : word} is set up`, {
        description: `Next time tap “Sign in with ${label}” — no password needed.`,
      });
    } catch (err) {
      const browser = describePasskeyError(err);
      if (browser.kind === 'exists') {
        writePasskeyChoice(userId, 'registered');
        setOpen(false);
      } else if (browser.kind !== 'cancelled') {
        notify.error('Couldn’t set it up', { description: browser.message || getErrorMessage(err) });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="passkey-offer-title"
      aria-describedby="passkey-offer-text"
      onKeyDown={onKeyDown}
      className="fixed inset-x-3 bottom-[calc(var(--app-bottom-nav-height,0px)+12px)] z-50 rounded-card border border-line bg-surface p-4 shadow-overlay sm:inset-x-auto sm:right-6 sm:w-[380px]"
      data-testid="passkey-offer"
    >
      <div className="flex items-start gap-3">
        <span className="ui-icon-tile tone-navy h-10 w-10 shrink-0 rounded-xl" aria-hidden="true">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="passkey-offer-title" className="text-[15px] font-bold text-ink">Use {label} next time?</h2>
          <p id="passkey-offer-text" className="mt-0.5 text-[13px] leading-snug text-ink-2">
            Sign in on this device with {label} instead of typing your password — handy on site.
          </p>
        </div>
        <button
          type="button"
          onClick={notNow}
          className="-mr-1 -mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-control text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Not now"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <Button ref={primaryRef} type="button" className="h-11 flex-1" onClick={setUp} disabled={saving} data-testid="passkey-offer-set-up">
          {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Icon aria-hidden="true" />}
          Set up {label}
        </Button>
        <Button type="button" variant="outline" className="h-11" onClick={notNow} disabled={saving} data-testid="passkey-offer-not-now">
          Not now
        </Button>
      </div>
    </div>
  );
}
